// Shared portal + body-scroll-lock primitives for bottom sheets and modals.
// Extracted from WeeklyLineupManager.js, which re-exports these for its
// existing imports (ReplaceTeamModal, ConfirmCutModal) so those call sites
// don't need to change.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

// How many open sheets currently want the page locked. A bid sheet opens on
// top of the team drawer, so locks nest: the page is released only when the
// last one closes, not when the inner sheet closes under a drawer still open.
let lockCount = 0;
let savedScrollY = 0;
let savedStyles = null;

// The page scrolls on the root element, not on body: index.css gives html
// `overflow-x: clip`, and once html's overflow is not `visible`, body's own
// overflow is no longer propagated to the viewport. `body { overflow: hidden }`
// therefore locked nothing, and the page behind every sheet kept scrolling.
// `touch-action: none` on body did not stop it on iOS either.
//
// Pinning body with position: fixed is what actually holds, on iOS Safari and
// in the Capacitor WebView alike. Fixing body throws the page back to the top,
// so the scroll offset is carried in `top` and restored on release.
function lock() {
  lockCount += 1;
  if (lockCount > 1) return;

  const html = document.documentElement;
  const { body } = document;
  savedScrollY = window.scrollY;
  savedStyles = {
    htmlOverflow: html.style.overflow,
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
    overflow: body.style.overflow,
  };

  html.style.overflow = 'hidden';
  body.style.position = 'fixed';
  body.style.top = `-${savedScrollY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
}

function unlock() {
  if (lockCount === 0) return;
  lockCount -= 1;
  if (lockCount > 0 || !savedStyles) return;

  const html = document.documentElement;
  const { body } = document;
  html.style.overflow = savedStyles.htmlOverflow;
  body.style.position = savedStyles.position;
  body.style.top = savedStyles.top;
  body.style.left = savedStyles.left;
  body.style.right = savedStyles.right;
  body.style.width = savedStyles.width;
  body.style.overflow = savedStyles.overflow;
  savedStyles = null;
  // 'instant', so a smooth-scroll stylesheet cannot animate the page back
  // down from the top when the sheet closes.
  window.scrollTo({ top: savedScrollY, behavior: 'instant' });
}

export function useLockBodyScroll(locked) {
  useEffect(() => {
    if (!locked) return undefined;
    lock();
    return unlock;
  }, [locked]);
}

// The wrapper carries .theme-auto because a portal mounts at document.body,
// outside whatever page opened it. Dark mode is scoped to that class, so
// without this a sheet opened from a dark page resolved every token to its
// light value and came up white.
//
// It is unconditional rather than copied from the opener: on a page that has
// not been migrated the tokens resolve to the light values anyway, so the
// class changes nothing there.
export const ModalPortal = ({ open, children }) =>
  open ? createPortal(<div className="theme-auto">{children}</div>, document.body) : null;
